export type Period = "today" | "yest" | "7d" | "30d" | "all";

export interface DashboardKPI {
  period_label: string;
  total_slots: number;
  completed: number;
  missed: number;
  emergency: number;
  late: number;
  completion_pct: number;
  avg_response_sec: number | null;
  avg_fill_sec: number | null;
  kw_min: number | null;
  kw_max: number | null;
  kv_min: number | null;
  kv_max: number | null;
  turbines: TurbineDashboardStatus[];
  kw_sparkline: KwPoint[];
}

export interface TurbineDashboardStatus {
  id: number;
  name: string;
  station_name: string | null;
  is_active: boolean;
  is_running: boolean;
  last_kw: number | null;
  last_kv: number | null;
  last_at: string | null;
  downtime_sec: number;
  kw_min: number | null;
  kw_max: number | null;
  kv_min: number | null;
  kv_max: number | null;
}

export interface KwPoint {
  t: string;
  kw: number;
}

export interface Measurement {
  id: number;
  station_id: number;
  user_id: number | null;
  user_full_name: string | null;
  slot_time: string;
  is_emergency: boolean;
  is_late: boolean;
  /** True if the turbine was stopped at submit time. Shown orange in UI/Excel. */
  is_offline: boolean;
  status: "pending" | "in_progress" | "completed" | "missed";
  notified_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  response_time_sec: number | null;
  fill_duration_sec: number | null;
  turbine_id: number | null;

  // === Legacy / power ===
  kw: number | null;
  kv: number | null;
  kvar: number | null;
  cos_phi: number | null;
  frequency: number | null;
  t_tr: number | null;
  bar: number | null;
  pipe_pressure: number | null;

  // === Generator / GLU / HPU temps & flows ===
  bt_330_1: number | null;
  bt_330_2: number | null;
  bt_331: number | null;
  bt_332: number | null;
  bt_371: number | null;
  bt_372: number | null;
  bt_373: number | null;
  bt_311: number | null;
  sf_341: number | null;
  sf_342: number | null;
  sf_371: number | null;
  sf_372: number | null;

  // === Pressures ===
  bp_371: number | null;
  bp_311: number | null;
  bp_312: number | null;
  pn_311: number | null;
  pn_312: number | null;
  pn_313: number | null;
  pn_314: number | null;
  pn_321: number | null;
  pn_322: number | null;
  pn_341: number | null;
  pn_342: number | null;
  pn_351: number | null;
  pn_352: number | null;
  pn_371: number | null;

  // === Line / voltage / current / excitation ===
  line_u_kv: number | null;
  voltage_r_kv: number | null;
  voltage_s_kv: number | null;
  voltage_t_kv: number | null;
  current_r: number | null;
  current_s: number | null;
  current_t: number | null;
  excitation_u_kv: number | null;
  turbine_position: number | null;
  dgs: number | null;

  // === TR 1 / TR 2 / ATR ===
  tr1_t1: number | null; tr1_t2: number | null; tr1_t3: number | null;
  tr2_t1: number | null; tr2_t2: number | null; tr2_t3: number | null;
  atr_t1: number | null; atr_t2: number | null; atr_t3: number | null;

  // === Boolean-ish (on/off, parallel/isolated) — stored as TEXT ===
  mode_parallel: string | null;
  mode_isolated: string | null;
  line_h01: string | null;
  line_h02: string | null;
  sq_321: string | null;
  sp_371: string | null;
  sp_372: string | null;
  sp_311: string | null;
  sp_312: string | null;
  en_371: string | null;
  en_311: string | null;
  f215_1: string | null;
  f215_2: string | null;

  // === Pair text "X/Y" ===
  bt_333_338: string | null;
  sl_371_372: string | null;
  sl_311_312: string | null;
  // Legacy pair fields kept for old rows
  bt_330: string | null;
  bt_333_336: string | null;
  bt_334_337: string | null;
  bt_335_338: string | null;

  turbine_name: string | null;
  // Emergency-only context
  emergency_reason: string | null;
}

// ---------- Turbines ----------

export interface Turbine {
  id: number;
  station_id: number;
  name: string;
  is_active: boolean;
  is_running: boolean;
  created_at: string;
}

export interface TurbineEvent {
  id: number;
  turbine_id: number;
  event_type: "start" | "stop";
  /** KW: filled on Stop events (and on legacy Start rows before migration 0008). */
  kw: number | null;
  /** KV: filled on Start events created after migration 0008. */
  kv: number | null;
  reason: string | null;
  event_time: string;
  created_by: number;
  created_at: string;
  turbine_name?: string | null;
  station_name?: string | null;
}

export interface TurbineStartBody {
  kv: string;
  event_time?: string;
}

export interface TurbineStopBody {
  kw: string;
  reason: string;
  event_time: string;
}

export interface Responsible {
  id: number;
  telegram_id: number;
  full_name: string;
  role: string;
  station_id: number | null;
  is_active: boolean;
  created_at: string;
}

export interface Shift {
  id: number;
  user_id: number;
  station_id: number;
  shift_date: string;
  created_by: number;
  created_at: string;
  skipped?: boolean;
}

export interface InviteResult {
  token: string;
  url: string;
  full_name: string;
  expires_at: string;
}

export interface BotSettings {
  notification_interval_min: number;  // 5..180 — minutes between bot pings
}

export interface MeOut {
  id: number;
  telegram_id: number;
  full_name: string;
  role: "admin" | "responsible";
  is_active: boolean;
  station_id: number | null;
  station_name: string | null;
  station_city: string | null;
  station_timezone: string | null;
  language_code: "ru" | "en" | "fa";
}

export interface MyStats {
  period_label: string;
  total_slots: number;
  completed: number;
  missed: number;
  emergency: number;
  late: number;
  completion_pct: number;
  avg_response_sec: number | null;
  avg_fill_sec: number | null;
}

export interface UserStat {
  user_id: number;
  full_name: string;
  total: number;
  completed: number;
  missed: number;
  emergency: number;
  late: number;
  avg_response_sec: number | null;
  avg_fill_sec: number | null;
}

export interface DayStat {
  day: string;
  completed: number;
  total: number;
  missed: number;
  emergency: number;
}

export interface HourStat {
  hour: number;
  completed: number;
  total: number;
}

export interface StatusDistribution {
  completed_on_time: number;
  completed_late: number;
  missed: number;
  pending_in_progress: number;
  emergency: number;
}

export interface StatsBundle {
  period_label: string;
  summary: DashboardKPI;
  by_user: UserStat[];
  by_day: DayStat[];
  by_hour: HourStat[];
  distribution: StatusDistribution;
}

// ---------- Responsible report flow ----------

export interface CurrentSlot {
  has_slot: boolean;
  measurement_id: number | null;
  slot_time: string | null;
  status: "pending" | "in_progress" | "completed" | "missed" | null;
  station_timezone: string | null;
  already_completed: boolean;
  message: string | null;
  is_day_off: boolean;
  shift_id: number | null;
  has_shift: boolean;
  /** False when the turbine is stopped — submission will be flagged offline. */
  is_turbine_running: boolean;
}

export interface ReportSubmitBody {
  turbine_id: number;
  values: { [fieldKey: string]: string };
}

export interface EmergencyBody {
  turbine_id: number;
  kw?: string;
  reason: string;
  /** Optional extras: full list of measurement field keys → raw string values */
  extra_values?: Record<string, string>;
}

/**
 * Single "KV" value for a measurement (dashboard tile + KW/KV charts + scatter).
 * The new SHPP form has no single voltage field, so coalesce:
 *   line U → mean of the phase voltages R/S/T → legacy kv (pre-Phase-8 rows).
 * Returns null if no voltage was recorded. All are ~20 kV, so it's coherent.
 */
export function kvOf(m: Measurement): number | null {
  if (m.line_u_kv != null) return m.line_u_kv;
  const phases = [m.voltage_r_kv, m.voltage_s_kv, m.voltage_t_kv]
    .filter((v): v is number => v != null);
  if (phases.length) return phases.reduce((a, b) => a + b, 0) / phases.length;
  // Legacy kv last, and only if plausible. Valid historical readings are all
  // <= 30 kV; a few old rows hold data-entry errors (91, 1065, 1960 — KW typed
  // into KV). 50 kV is the natural cutoff for this ~20 kV station.
  if (m.kv != null && m.kv < 50) return m.kv;
  return null;
}

export type FieldKind = "single" | "pair" | "bool";

export interface MeasurementField {
  key: string;
  label: string;
  unit?: string;
  is_pair: boolean;
  /** "single" = numeric, "pair" = "X/Y", "bool" = on/off / ok/no */
  kind: FieldKind;
  /** Category key from FIELD_CATEGORIES (groups fields in the form). */
  category: string;
}

export interface FieldCategory {
  key: string;
  label: string;
}

// Field categories — order = display order in the operator form.
export const FIELD_CATEGORIES: FieldCategory[] = [
  { key: "mode",          label: "MODE" },
  { key: "dgs",           label: "DGS" },
  { key: "line",          label: "Line (kV)" },
  { key: "turbine_pos",   label: "Turbine position" },
  { key: "pressure_mode", label: "Pressure mode (bar)" },
  { key: "power",         label: "Power" },
  { key: "current",       label: "Current (A)" },
  { key: "voltage",       label: "Voltage (kV)" },
  { key: "excitation",    label: "Excitation" },
  { key: "generator",     label: "Generator (°C)" },
  { key: "turbine",       label: "Turbine" },
  { key: "glu",           label: "GLU" },
  { key: "hpu",           label: "HPU" },
  { key: "cooling_water", label: "Cooling water" },
  { key: "tr1",           label: "TR 1 (°C)" },
  { key: "tr2",           label: "TR 2 (°C)" },
  { key: "atr",           label: "ATR (°C)" },
];

const _s = (key: string, label: string, unit: string, category: string): MeasurementField =>
  ({ key, label, unit: unit || undefined, is_pair: false, kind: "single", category });
const _p = (key: string, label: string, unit: string, category: string): MeasurementField =>
  ({ key, label, unit: unit || undefined, is_pair: true, kind: "pair", category });
const _b = (key: string, label: string, category: string): MeasurementField =>
  ({ key, label, is_pair: false, kind: "bool", category });

// Full SHPP indicator set (65 fields, 17 categories) mirroring the
// "ведомость показаний" form provided by the operator. Order matters —
// this is also the display order in form, custom-preview and Excel.
export const MEASUREMENT_FIELDS: MeasurementField[] = [
  // MODE
  _b("mode_parallel",    "MODE parallel", "mode"),
  _b("mode_isolated",    "MODE isolated", "mode"),
  // DGS
  _s("dgs",              "DGS", "",              "dgs"),
  // Line
  _b("line_h01",         "H01",                  "line"),
  _b("line_h02",         "H02",                  "line"),
  _s("line_u_kv",        "U",         "kV",      "line"),
  // Turbine position
  _s("turbine_position", "Turbine position", "%", "turbine_pos"),
  // Pressure mode
  _s("pn_351",           "PN 351",    "bar",     "pressure_mode"),
  _s("pn_352",           "PN 352",    "bar",     "pressure_mode"),
  // Power
  _s("kw",               "kW",        "kW",      "power"),
  _s("kvar",             "kVAr",      "kVAr",    "power"),
  _s("cos_phi",          "cos φ",     "",        "power"),
  _s("frequency",        "Hz",        "Hz",      "power"),
  // Current
  _s("current_r",        "R",         "A",       "current"),
  _s("current_s",        "S",         "A",       "current"),
  _s("current_t",        "T",         "A",       "current"),
  // Voltage by phase
  _s("voltage_r_kv",     "R",         "kV",      "voltage"),
  _s("voltage_s_kv",     "S",         "kV",      "voltage"),
  _s("voltage_t_kv",     "T",         "kV",      "voltage"),
  // Excitation
  _s("excitation_u_kv",  "U",         "kV",      "excitation"),
  // Generator
  _s("bt_330_1",         "BT 330.1",  "°C",      "generator"),
  _s("bt_330_2",         "BT 330.2",  "°C",      "generator"),
  _s("bt_331",           "BT 331",    "°C",      "generator"),
  _s("bt_332",           "BT 332",    "°C",      "generator"),
  _p("bt_333_338",       "BT 333–BT 338 min/max", "°C", "generator"),
  // Turbine
  _s("sf_341",           "SF 341",    "l/min",   "turbine"),
  _s("pn_321",           "PN 321",    "bar",     "turbine"),
  _s("pn_322",           "PN 322",    "bar",     "turbine"),
  _b("sq_321",           "SQ 321",                "turbine"),
  // GLU
  _s("sf_342",           "SF 342",    "l/min",   "glu"),
  _s("sf_371",           "SF 371",    "l/min",   "glu"),
  _s("sf_372",           "SF 372",    "l/min",   "glu"),
  _s("bp_371",           "BP 371",    "bar",     "glu"),
  _s("pn_371",           "PN 371",    "bar",     "glu"),
  _b("sp_371",           "SP 371",                "glu"),
  _b("sp_372",           "SP 372",                "glu"),
  _s("bt_371",           "BT 371",    "°C",      "glu"),
  _s("bt_372",           "BT 372",    "°C",      "glu"),
  _s("bt_373",           "BT 373",    "°C",      "glu"),
  _p("sl_371_372",       "SL 371-SL 372", "mm",  "glu"),
  _b("en_371",           "EN 371",                "glu"),
  // HPU
  _s("bp_311",           "BP 311",    "bar",     "hpu"),
  _s("bp_312",           "BP 312",    "bar",     "hpu"),
  _s("pn_311",           "PN 311",    "bar",     "hpu"),
  _s("pn_312",           "PN 312",    "bar",     "hpu"),
  _s("pn_313",           "PN 313",    "bar",     "hpu"),
  _s("pn_314",           "PN 314",    "bar",     "hpu"),
  _b("sp_311",           "SP 311",                "hpu"),
  _b("sp_312",           "SP 312",                "hpu"),
  _s("bt_311",           "BT 311",    "°C",      "hpu"),
  _p("sl_311_312",       "SL 311-SL 312", "mm",  "hpu"),
  _b("en_311",           "EN 311",                "hpu"),
  // Cooling water
  _s("pn_341",           "PN 341",    "bar",     "cooling_water"),
  _s("pn_342",           "PN 342",    "bar",     "cooling_water"),
  _b("f215_1",           "F215/1",                "cooling_water"),
  _b("f215_2",           "F215/2",                "cooling_water"),
  // TR 1 / TR 2 / ATR
  _s("tr1_t1",           "TR 1 / 1",  "°C",      "tr1"),
  _s("tr1_t2",           "TR 1 / 2",  "°C",      "tr1"),
  _s("tr1_t3",           "TR 1 / 3",  "°C",      "tr1"),
  _s("tr2_t1",           "TR 2 / 1",  "°C",      "tr2"),
  _s("tr2_t2",           "TR 2 / 2",  "°C",      "tr2"),
  _s("tr2_t3",           "TR 2 / 3",  "°C",      "tr2"),
  _s("atr_t1",           "ATR / 1",   "°C",      "atr"),
  _s("atr_t2",           "ATR / 2",   "°C",      "atr"),
  _s("atr_t3",           "ATR / 3",   "°C",      "atr"),
];

// ---------- Автоматика (SCADA telemetry, separate subsystem) ----------

export interface AutoMetric { key: string; label: string; unit: string; group: string; col: number; }
export interface AutoGroup { key: string; label: string; }
export interface AutoCatalog { groups: AutoGroup[]; metrics: AutoMetric[]; data_from: string | null; data_to: string | null; }

export interface AutoKpiMetric { min: number | null; avg: number | null; max: number | null; last?: number | null; }
export interface AutoKpi {
  unit: number;
  from_ts: string; to_ts: string;
  data_minutes: number; running_minutes: number; running_pct: number;
  energy_kwh: number;
  metrics: Record<string, AutoKpiMetric>;
}
export interface AutoSeriesPoint { t: string; min: number | null; avg: number | null; max: number | null; flag: boolean; }
export interface AutoSeries {
  metric: string; label: string; unit: string;
  bucket_min: number; resolution: string;
  mean: number | null; std: number | null;
  points: AutoSeriesPoint[];
}
export interface AutoAnomaly {
  metric: string; label: string; unit: string; group: string;
  start: string; end: string; peak: number; mean: number; std: number;
  delta: number; rel_pct: number; max_z: number; direction: "high" | "low";
}
export interface AutoAnomalies { sigma: number; rel_floor_pct: number; count: number; truncated: boolean; events: AutoAnomaly[]; }
export interface AutoSnapshotMetric { min: number | null; avg: number | null; max: number | null; }
export interface AutoSnapshot { at: string; unit: number; metrics: Record<string, AutoSnapshotMetric>; }
export interface AutoImportRow {
  id: number; filename: string; unit: number; rows: number; minutes: number;
  from_ts: string | null; to_ts: string | null; status: string; created_at: string;
}
